const benefits = [
  {
    icon: "fas fa-clock",
    title: "Minimal Time Commitment",
    desc: "Just 3 hours per week. It fits perfectly around your life — you don't need to sacrifice your schedule.",
    image:
      "https://public.readdy.ai/ai/img_res/adc423c1754080bf62c4d73ab4552deb.jpg",
  },
  {
    icon: "fas fa-shopping-bag",
    title: "Products You Actually Use",
    desc: "These aren't random products — everyday essentials like cleaners, personal care, diapers, and more.",
    image:
      "https://readdy.ai/api/search-image?query=eco%20friendly%20natural%20cleaning%20products%20spray%20bottles%20with%20green%20and%20yellow%20liquid%2C%20blue%20microfibre%20cloth%2C%20against%20a%20clean%20white%20wall%2C%20minimal%20product%20photography%2C%20bright%20natural%20light%2C%20sustainable%20living&width=400&height=220&seq=benefit-products&orientation=landscape",
  },
  {
    icon: "fas fa-robot",
    title: "Automation Does the Heavy Lifting",
    desc: "Simple digital tools handle repetitive tasks. Landing pages, email sequences, and systems help you focus on what matters.",
    image:
      "https://readdy.ai/api/search-image?query=modern%20home%20office%20desk%20with%20open%20laptop%2C%20large%20green%20tropical%20house%20plant%2C%20stylish%20black%20pendant%20lamp%2C%20warm%20afternoon%20light%20through%20window%2C%20cozy%20productive%20workspace%2C%20interior%20lifestyle%20photography&width=400&height=220&seq=benefit-automation&orientation=landscape",
  },
];

const testimonials = [
  {
    text: '"I started making $400 extra a month within 90 days, just by sharing products I already used. Carol made it so simple."',
    name: "Sarah M.",
    role: "Mom of 3, Teacher",
    image:
      "https://readdy.ai/api/search-image?query=friendly%20young%20woman%20smiling%20portrait%20headshot%20natural%20light%20brown%20hair%20casual%20warm%20tone&width=48&height=48&seq=testi-sarah&orientation=squarish",
  },
  {
    text: '"The green swaps alone changed how I feel about cleaning my home. No more toxic smells — my kids can actually help now!"',
    name: "Jessica R.",
    role: "Stay-at-home Mom",
    image:
      "https://readdy.ai/api/search-image?query=beautiful%20Black%20woman%20portrait%20headshot%20smiling%20warmly%20natural%20window%20light%20glowing%20melanin%20skin%20tone%20short%20natural%20curls%20casual%20beige%20top%20soft%20neutral%20background%20lifestyle%20photography&width=48&height=48&seq=testi-jessica-v2&orientation=squarish",
  },
  {
    text: '"I was so skeptical at first. But after 6 months, this has become a real income stream for our family. Totally worth it."',
    name: "Amanda K.",
    role: "Nurse & Entrepreneur",
    image:
      "https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20headshot%20portrait%20natural%20light%20confident%20warm%20friendly%20expression&width=48&height=48&seq=testi-amanda&orientation=squarish",
  },
  {
    text: '"As a busy working mom, I needed something flexible. This fits my life perfectly and the products are genuinely amazing."',
    name: "Dana W.",
    role: "Working Mom of 2",
    image:
      "https://readdy.ai/api/search-image?query=friendly%20Black%20woman%20smiling%20headshot%20portrait%20natural%20light%20warm%20skin%20tone%20casual%20lifestyle&width=48&height=48&seq=testi-dana&orientation=squarish",
  },
];

import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function BenefitsSection() {
  const { ref, visible } = useScrollReveal();

  return (
    <section id="benefits" className="py-20 bg-white">
      <div ref={ref} className={`mx-auto px-4 md:px-6 max-w-6xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'}`}>
        <div className="flex justify-center mb-5">
          <span className="inline-flex items-center gap-2 border border-olive-300 text-olive-600 text-xs font-semibold px-4 py-1.5 rounded-full tracking-wider uppercase">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="far fa-heart text-xs"></i>
            </span>
            Why It Works
          </span>
        </div>
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-olive-900 mb-4">
            Why Families Choose This Path
          </h2>
          <p className="text-olive-500 text-base max-w-lg mx-auto">
            <strong>Real</strong> results for real families — without the
            complicated strategies or sleepless nights.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {benefits.map((b, i) => (
            <div
              key={i}
              className="rounded-2xl border border-olive-100 overflow-hidden flex flex-col"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  alt={b.title}
                  className="w-full h-full object-cover object-top"
                  src={b.image}
                />
              </div>
              <div className="p-6 flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-olive-100 rounded-xl text-olive-500 text-base shrink-0">
                    <i className={b.icon}></i>
                  </div>
                  <h3 className="font-heading font-bold text-olive-900 text-base leading-snug">
                    {b.title}
                  </h3>
                </div>
                <p className="text-olive-600 text-sm leading-relaxed">
                  {b.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-olive-50 rounded-2xl p-6 flex flex-col gap-4"
            >
              <div className="flex items-center gap-1 text-yellow-400">
                {[...Array(5)].map((_, j) => (
                  <span
                    key={j}
                    className="w-3 h-3 flex items-center justify-center"
                  >
                    <i className="fas fa-star text-sm"></i>
                  </span>
                ))}
              </div>
              <p className="text-olive-700 text-sm leading-relaxed italic flex-1">
                {t.text}
              </p>
              <div className="flex items-center gap-3 pt-1">
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                  <img
                    alt={t.name}
                    className="w-full h-full object-cover object-top"
                    src={t.image}
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-olive-900">{t.name}</p>
                  <p className="text-xs text-olive-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}